export type DaySource = 'task' | 'email' | 'sign' | 'call' | 'meeting' | 'dm'

export interface DayItemLite {
  id: string
  source: DaySource
  status: string            // 'open' | 'done' | 'overdue'
  occurredAt: string
  dueAt?: string
  closedAt?: string
}

// Block order on the page. Calls + meetings live in the agenda, never in a block.
export const BLOCK_SOURCES: DaySource[] = ['email', 'task', 'dm', 'sign']

export const BLOCK_META: Record<string, { label: string }> = {
  email: { label: 'Mail · needs reply' },
  task: { label: 'Tasks' },
  dm: { label: 'Direct messages' },
  sign: { label: 'Signatures' },
}

function plannerDate(i: DayItemLite): string { return String(i.dueAt ?? i.occurredAt).slice(0, 10) }

export function itemsForDay<T extends DayItemLite>(items: T[], day: string, today: string): T[] {
  return items.filter((i) => {
    const pd = plannerDate(i)
    if (pd === day) return true
    if (day === today && pd < today && i.status !== 'done') return true
    return false
  })
}

// An item belongs to the timed agenda when it is anchored to a clock time:
// every meeting/call, plus any item carrying a dueAt deadline.
export function isTimed(i: DayItemLite): boolean {
  if (i.source === 'meeting' || i.source === 'call') return true
  return !!i.dueAt
}

function timeOf(i: DayItemLite): string { return String(i.dueAt ?? i.occurredAt) }

export interface GroupedDay<T extends DayItemLite> {
  agenda: T[]
  blocks: Partial<Record<DaySource, T[]>>
  done: T[]
}

export function groupDay<T extends DayItemLite>(items: T[], day: string, today: string): GroupedDay<T> {
  const forDay = itemsForDay(items, day, today)
  const done = forDay.filter((i) => i.status === 'done')
  const open = forDay.filter((i) => i.status !== 'done')

  const agenda = open.filter(isTimed).sort((a, b) => timeOf(a).localeCompare(timeOf(b)))

  const blocks: Partial<Record<DaySource, T[]>> = {}
  for (const i of open) {
    if (isTimed(i)) continue
    ;(blocks[i.source] ||= []).push(i)
  }
  return { agenda, blocks, done }
}
